import type {
  ProxVoiceAvatarColor,
  ProxVoiceProfile,
  ProxVoiceSocialLinks,
} from "./services/proxvoice";
import {
  normalizePublicConnections,
  type PublicConnection,
} from "../components/profile/profile-connections";

export interface PublicProfileDraft {
  displayName: string;
  avatarUrl: string | null;
  avatarColor: ProxVoiceAvatarColor;
  bio: string;
  position: string;
  broker: string;
  socialLinks: ProxVoiceSocialLinks;
  publicConnections: PublicConnection[];
}

export interface PublicProfileUpdatePayload {
  displayName: string;
  avatarUrl: string | null;
  avatarColor: ProxVoiceAvatarColor;
  bio: string;
  position: string;
  broker: string;
  socialLinks: ProxVoiceSocialLinks;
  publicConnections: PublicConnection[];
}

export const PUBLIC_PROFILE_PERSISTED_FIELDS = [
  "displayName",
  "avatarUrl",
  "avatarColor",
  "bio",
  "position",
  "broker",
  "socialLinks",
  "publicConnections",
] as const;

export const PUBLIC_PROFILE_STORAGE_KEY = "fintheon:public-profile";
export const PUBLIC_PROFILE_UPDATED_EVENT = "fintheon:public-profile-updated";

export function emptyPublicProfileDraft(): PublicProfileDraft {
  return {
    displayName: "",
    avatarUrl: null,
    avatarColor: "orange",
    bio: "",
    position: "",
    broker: "",
    socialLinks: {},
    publicConnections: [],
  };
}

export function normalizePublicProfileDraft(
  draft: Partial<PublicProfileDraft>,
): PublicProfileDraft {
  return {
    displayName: cleanTextValue(draft.displayName, 32),
    avatarUrl:
      typeof draft.avatarUrl === "string" && draft.avatarUrl.trim()
        ? draft.avatarUrl.trim()
        : null,
    avatarColor: normalizeAvatarColor(draft.avatarColor),
    bio: cleanTextValue(draft.bio, 180),
    position: cleanTextValue(draft.position, 48),
    broker: cleanTextValue(draft.broker, 48),
    socialLinks: normalizeSocialLinks(draft.socialLinks),
    publicConnections: normalizePublicConnections(draft.publicConnections),
  };
}

export function publicProfileDraftFromProxVoice(
  profile: ProxVoiceProfile,
): PublicProfileDraft {
  return normalizePublicProfileDraft({
    displayName: profile.displayName,
    avatarUrl: profile.avatarUrl,
    avatarColor: profile.avatarColor,
    bio: profile.bio ?? "",
    position: profile.position ?? "",
    broker: profile.broker ?? "",
    socialLinks: profile.socialLinks,
    publicConnections: normalizePublicConnections(profile.publicConnections),
  });
}

export function publicProfileUpdatePayload(
  draft: PublicProfileDraft,
): PublicProfileUpdatePayload {
  const normalized = normalizePublicProfileDraft(draft);
  return {
    ...normalized,
    displayName: normalized.displayName.trim(),
    bio: normalized.bio.trim(),
    position: normalized.position.trim(),
    broker: normalized.broker.trim(),
  };
}

export function readPublicProfileDraft(): PublicProfileDraft {
  if (typeof localStorage === "undefined") return emptyPublicProfileDraft();
  try {
    const stored = localStorage.getItem(PUBLIC_PROFILE_STORAGE_KEY);
    if (!stored) return emptyPublicProfileDraft();
    return normalizePublicProfileDraft(JSON.parse(stored));
  } catch {
    return emptyPublicProfileDraft();
  }
}

export function writePublicProfileDraft(draft: PublicProfileDraft) {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(
    PUBLIC_PROFILE_STORAGE_KEY,
    JSON.stringify(normalizePublicProfileDraft(draft)),
  );
}

export function notifyPublicProfileUpdated(draft: PublicProfileDraft) {
  window.dispatchEvent(
    new CustomEvent<PublicProfileDraft>(PUBLIC_PROFILE_UPDATED_EVENT, {
      detail: normalizePublicProfileDraft(draft),
    }),
  );
}

function normalizeSocialLinks(
  links: ProxVoiceSocialLinks | undefined,
): ProxVoiceSocialLinks {
  return {
    x: cleanSocialValue(links?.x),
    substack: cleanSocialValue(links?.substack),
    telegram: cleanSocialValue(links?.telegram),
    discord: cleanSocialValue(links?.discord),
  };
}

function cleanTextValue(value: unknown, maxLength: number): string {
  return typeof value === "string" ? value.slice(0, maxLength) : "";
}

function cleanSocialValue(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const next = value.replace(/\s+/g, "").trim().replace(/^@+/, "");
  return next ? next.slice(0, 80) : undefined;
}

function normalizeAvatarColor(value: unknown): ProxVoiceAvatarColor {
  if (
    value === "orange" ||
    value === "goldenrod" ||
    value === "teal" ||
    value === "light-green" ||
    value === "purple"
  ) {
    return value;
  }
  return "orange";
}
